import * as React from "react";
import type { EditorState } from "react-draft-wysiwyg";
import {
    DateTimeInput,
    Select,
    type SelectOptionType,
    Toggle,
} from "@onshopfront/components";
import type { Moment } from "moment";
import moment from "moment";
import { compare } from "#/Utilities/Generic/Objects.ts";
import type { MDBTypes } from "#/Utilities/Hooks/MDBHooks.ts";
import CurrencyInput from "../Forms/CurrencyInput";
import { convertHTMLToDraft, HTMLEditor } from "../Forms/HTMLEditor/HTMLEditor";
import PercentageInput from "../Forms/PercentageInput";
import RetractableInput from "../Forms/RetractableInput";
import Media from "../Media/Media";
import { MDBSelect } from "../Select/MDBSelect";
import type { EditSectionSourceType } from "./EditPage";

export type EditPageFieldValueType =
    string |
    boolean |
    number |
    SelectOptionType<string | number> |
    Array<string | number> |
    Array<SelectOptionType<string | number>> |
    EditorState |
    Moment |
    null;

type ExportPageMap = {
    string: string;
    number: number;
    text: string;
    select: SelectOptionType | Array<SelectOptionType> | null;
    toggle: boolean;
    html: EditorState;
    currency: string;
    date: Moment | null;
    image: string | null;
    percentage: string;
};

export type EditPageFieldType = keyof ExportPageMap;

type Props<TType extends EditPageFieldType, TVal = ExportPageMap[TType]> = typeof EditPageField.defaultProps & {
    label: string;
    field: string;
    value?: TVal;
    type: TType;
    onChange: (field: string, value: TVal) => void;
    options?: Array<SelectOptionType<string>>;
    loadOptions?: (search: string) => Promise<Array<SelectOptionType<string>>>;
    selectedOption?: SelectOptionType<string>;
    multiple?: boolean;
    onCreate?: (field: string, value: string, callback: (label: string, value: string) => void) => void;
    source?: EditSectionSourceType;
    placeholder?: string;
    // disabled?: boolean;
    // clearable?: boolean;
    // autoFocus?: boolean;
};

interface State {
    optionValue: null | SelectOptionType<number | string> | Array<SelectOptionType<number | string>>;
    htmlParsed: boolean;
}

export default class EditPageField<
    TType extends EditPageFieldType,
    TVal = ExportPageMap[TType]
> extends React.PureComponent<Props<TType, TVal>, State> {
    public static defaultProps = {
        disabled : false,
        clearable: true,
        autoFocus: false,
    };

    constructor(props: Props<TType, TVal>) {
        super(props);

        this.state = {
            optionValue: props.selectedOption || null,
            htmlParsed : false,
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectChange = this.handleSelectChange.bind(this);
        this.handleToggleChange = this.handleToggleChange.bind(this);
        this.handleHTMLChange = this.handleHTMLChange.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    public componentDidMount(): void {
        if(this.props.type === "html" && this.props.value) {
            this.props.onChange(this.props.field, convertHTMLToDraft(this.props.value as string) as TVal);
            this.setState({
                htmlParsed: true,
            });
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props<TType, TVal>>): void {
        if(!compare(prevProps.selectedOption, this.props.selectedOption)) {
            this.setState({
                optionValue: this.props.selectedOption || null,
            });
        }
    }

    protected hasValue(): boolean {
        if(this.props.placeholder) {
            return true;
        }

        if(this.props.selectedOption && this.props.selectedOption.value) {
            return true;
        }

        if(this.state.optionValue) {
            if(Array.isArray(this.state.optionValue) && this.state.optionValue.length > 1) {
                return true;
            } else if((this.state.optionValue as SelectOptionType<string>).value) {
                return true;
            }
        }

        if(typeof this.props.value === "number" && this.props.value === 0) {
            return true;
        }

        if(this.props.type === "html" || this.props.type === "image") {
            return true;
        }

        if(Array.isArray(this.props.value)) {
            return !!this.props.value.length;
        }

        return !!this.props.value;
    }

    protected handleInputChange(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        this.props.onChange(this.props.field, e.currentTarget.value as TVal);
    }

    protected handleSelectChange(
        option: null | SelectOptionType<string | number> | Array<SelectOptionType<string | number>>
    ): void {
        this.setState({
            optionValue: option,
        });

        if(Array.isArray(option)) {
            this.props.onChange(
                this.props.field,
                option.map(opt => opt.value) as TVal
            );

            return;
        }

        this.props.onChange(this.props.field, (option === null ? option : option.value) as TVal);
    }

    protected handleToggleChange(e: React.FormEvent<HTMLInputElement>): void {
        this.props.onChange(this.props.field, e.currentTarget.checked as TVal);
    }

    protected handleHTMLChange(editorState: EditorState): void {
        this.props.onChange(this.props.field, editorState as TVal);
    }

    protected handleMediaChange(url: string | null): void {
        this.props.onChange(this.props.field, url as TVal);
    }

    protected handleCreate(value: string): Promise<SelectOptionType<string>> {
        return new Promise<SelectOptionType<string>>((res, rej) => {
            if(typeof this.props.onCreate !== "function") {
                return rej("Field is not creatable");
            }

            this.props.onCreate(this.props.field, value, (label: string, value: string) => {
                res({
                    label,
                    value,
                });
            });
        });
    }

    protected renderString(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <input
                type="text"
                onChange={this.handleInputChange}
                value={typeof this.props.value === "undefined" ? "" : this.props.value as string}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={this.props.disabled}
                data-testid="edit-page-field-string"
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderNumber(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <input
                type="number"
                step="any"
                onChange={this.handleInputChange}
                value={typeof this.props.value === "undefined" ? "" : this.props.value as string}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={this.props.disabled}
                data-testid="edit-page-field-number"
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderCurrency(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <CurrencyInput
                onChange={this.handleInputChange}
                value={typeof this.props.value === "undefined" ? "" : this.props.value as string}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={this.props.disabled}
                data-testid="edit-page-field-currency"
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderPercentage(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <PercentageInput
                onChange={this.handleInputChange}
                value={typeof this.props.value === "undefined" ? "" : this.props.value as string}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={this.props.disabled}
                data-testid="edit-page-field-percentage"
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderText(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <textarea
                onChange={this.handleInputChange}
                value={this.props.value as string || ""}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={this.props.disabled}
                data-testid="edit-page-field-text"
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderSelect(onFocus: () => void, onBlur: () => void): React.ReactNode {
        if(typeof this.props.source !== "undefined" && this.props.source.type === "mdb") {
            return (
                <MDBSelect
                    type={this.props.source.source as MDBTypes}
                    onChange={this.handleSelectChange}
                    isClearable={this.props.clearable}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    mdbId={(this.state.optionValue as SelectOptionType<number> | null)?.value ?? null}
                    placeholder={this.props.placeholder || ""}
                    data-testid="edit-page-field-mdb-select"
                    autoFocus={this.props.autoFocus}
                />
            );
        }

        return (
            <Select
                onChange={this.handleSelectChange}
                isClearable={this.props.clearable}
                onFocus={onFocus}
                onBlur={onBlur}
                value={this.state.optionValue}
                placeholder={this.props.placeholder || ""}
                isMulti={this.props.multiple}
                canCreate={typeof this.props.onCreate === "function"}
                onCreate={this.handleCreate}
                data-testid="edit-page-field-select"
                disabled={this.props.disabled}
                autoFocus={this.props.autoFocus}
                /* components={{
                    MultiValueContainer: (props: any): React.ReactNode => (
                        <div className="edit-page-section-field-trackable">
                            <components.MultiValueContainer {...props} />
                        </div>
                    ),
                }}*/
                options={async (search: string): Promise<any> => {
                    if(typeof this.props.loadOptions === "function") {
                        return this.props.loadOptions(search);
                    } else if(typeof this.props.options === "undefined") {
                        return;
                    }

                    const results = [];

                    for(let i = 0, l = this.props.options.length; i < l; i++) {
                        const label = this.props.options[i].label as string;

                        if(label.toLowerCase().includes(search)) {
                            results.push(this.props.options[i]);
                        }
                    }

                    return results;
                }}
            />
        );
    }

    protected renderToggle(): React.ReactNode {
        return (
            <Toggle
                disabled={this.props.disabled}
                checked={!!this.props.value}
                onChange={this.handleToggleChange}
                data-testid="edit-page-field-toggle"
                autoFocus={this.props.autoFocus}
            />
        );
    }

    protected renderDate(onFocus: () => void, onBlur: () => void): React.ReactNode {
        return (
            <DateTimeInput
                onChange={([ start ]) => {
                    this.props.onChange(this.props.field, (start ? moment(start) : null) as TVal);
                }}
                onBlur={onBlur}
                onFocus={onFocus}
                value={[ (this.props.value as Moment | null)?.toDate() ?? null ]}
                placeholder=""
                closeOnSelect
            />
        );
    }

    protected renderHTML(): React.ReactNode {
        return (
            <HTMLEditor
                value={this.state.htmlParsed ? this.props.value as EditorState : undefined}
                onChange={this.handleHTMLChange}
                readOnly={!this.state.htmlParsed}
                data-testid="edit-page-field-html-editor"
                toolbarOnFocus
                toolbarClassName="edit-page-field-html-editor-toolbar"
                wrapperClassName="edit-page-field-html-editor-wrapper"
            />
        );
    }

    protected renderMedia(): React.ReactNode {
        return (
            <Media
                onChange={this.handleMediaChange}
                type="image"
                removable
                hideRemove
                source={this.props.value as string}
                noSource={(
                    <div className="input-media-selector">
                        <p>Select Image</p>
                    </div>
                )}
            />
        );
    }

    protected renderField(onFocus: () => void, onBlur: () => void): React.ReactNode {
        switch(this.props.type) {
            case "string":
                return this.renderString(onFocus, onBlur);
            case "number":
                return this.renderNumber(onFocus, onBlur);
            case "text":
                return this.renderText(onFocus, onBlur);
            case "select":
                return this.renderSelect(onFocus, onBlur);
            case "currency":
                return this.renderCurrency(onFocus, onBlur);
            case "percentage":
                return this.renderPercentage(onFocus, onBlur);
            case "date":
                return this.renderDate(onFocus, onBlur);
            case "html":
                return this.renderHTML();
            case "image":
                return this.renderMedia();
            default:
                throw new Error(`${this.props.type} has not been implemented`);
        }
    }

    public render(): React.ReactNode {
        const classes = [
            "edit-page-section-field",
            "edit-page-section-field-" + this.props.type,
        ];

        let left;

        if(this.props.type === "currency") {
            left = "2rem";
        }

        return (
            <div className={classes.join(" ")}>
                {this.props.type !== "toggle" && (
                    <RetractableInput
                        hasValue={this.hasValue()}
                        label={this.props.label}
                        left={left}
                    >
                        {
                            (
                                onFocus: () => void,
                                onBlur: () => void
                            ): React.ReactNode => this.renderField(onFocus, onBlur)
                        }
                    </RetractableInput>
                )}
                {this.props.type === "toggle" && (
                    <label className="flex items-center gap-2">
                        {this.renderToggle()}
                        <span className={(this.props.disabled ? "disabled" : "")}>
                            {this.props.label}
                        </span>
                    </label>
                )}
            </div>
        );
    }
}
